import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, postForm } from '@/lib/api'
import { googleTestState, waitingLabel, webhookUrl } from '@/lib/adConnections'
import { BASE } from '@/lib/base'
import { useCountry } from '@/lib/country'
import { vipRuleReady } from '@/lib/leadTiers'
import { VipAssignmentFields } from '@/components/VipAssignment'
import { dateTime } from '@/lib/format'
import { CHANNEL_LABELS } from '@/lib/leads'
import { useAsync } from '@/lib/useAsync'
import { PicklistField } from '@/components/PicklistField'
import { PageHeader, Tabs } from '@/components/ui/page'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, cx } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Select, Switch } from '@/components/ui/form'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  AdIntakeStatus,
  AdLeadSourceItem,
  AdLeadSourceKey,
  GoogleCsvResult,
  LeadAssignee,
  LeadAssignmentMode,
  LeadChannel,
  LeadInboundItem,
  LeadInboundStatus,
  MetaFormsResult,
  Paged,
  SalesTeamListItem,
  SaveAdLeadSourceRequest,
} from '@/types/api'

/**
 * Where Meta and Google Ads deliver leads, and what happened to the ones that did not become
 * leads.
 *
 * <p>
 * Each source maps a platform's form to a country and a rule for who gets its leads. Country
 * comes from here and never from what the person typed: a Meta form nobody has mapped holds
 * its leads until somebody does, and mapping it releases every one of them at once. Nothing
 * secret is shown here - Meta's secrets live in Key Vault, and a Google key is shown once,
 * when it is made, and never again.
 * </p>
 */

type AdTab = 'sources' | 'log'

const ASSIGNMENT_LABELS: Record<LeadAssignmentMode, string> = {
  Unassigned: 'Unassigned queue',
  FixedUser: 'One person',
  RoundRobin: 'Team, in turn',
}

/** Copy, and say so - or say why not, so the person knows to select the text instead. */
function useCopy() {
  const toast = useToast()

  return async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.show(`${what} copied`, 'success')
    } catch {
      toast.show('Could not copy - select the text and copy it instead', 'error')
    }
  }
}

export function AdConnections() {
  const { current } = useCountry()
  const [tab, setTab] = useState<AdTab>('sources')

  const status = useAsync<AdIntakeStatus>((signal) => api.get<AdIntakeStatus>('/ad-lead-sources/status', undefined, signal), [])
  const sources = useAsync<AdLeadSourceItem[]>(
    (signal) => api.get<AdLeadSourceItem[]>('/ad-lead-sources', undefined, signal),
    [],
  )
  const log = useAsync<LeadInboundItem[]>(
    (signal) => api.get<LeadInboundItem[]>('/lead-inbound', undefined, signal),
    [],
  )

  // Every source in the caller's countries, narrowed to the one being looked at. The API
  // lists all of them so a Director across two countries sees both from either.
  const visible = (sources.data ?? []).filter((s) => !current || s.countryId === current.countryId)
  const meta = visible.filter((s) => s.channel === 'MetaAds')
  const google = visible.filter((s) => s.channel === 'GoogleAds')

  const reloadAll = () => {
    sources.reload()
    log.reload()
  }

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Ad connections"
        hint="Meta and Google Ads lead forms, mapped to a country and to who gets their leads."
      />

      {status.data && <StatusStrip status={status.data} />}

      <Tabs<AdTab>
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'sources', label: 'Sources', count: visible.length },
          { id: 'log', label: 'Intake log', count: (log.data ?? []).length },
        ]}
      />

      {tab === 'sources' ? (
        <>
          {sources.error && <ErrorState title={sources.error.title} detail={sources.error.detail} onRetry={sources.reload} />}
          <Busy busy={sources.loading}>
            <div className="flex flex-col gap-5">
              <MetaCard sources={meta} onChanged={reloadAll} />
              <GoogleCard sources={google} onChanged={reloadAll} />
            </div>
          </Busy>
        </>
      ) : (
        <IntakeLog log={log} onChanged={reloadAll} />
      )}
    </>
  )
}

/** Whether Meta can reach us at all, and the address to give it. */
function StatusStrip({ status }: { status: AdIntakeStatus }) {
  const copy = useCopy()
  const callback = webhookUrl(BASE, window.location.origin, status.metaCallbackPath)

  return (
    <Card className="mb-5">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4 text-[13px]">
        <div className="flex items-center gap-2">
          <Badge tone={status.metaConfigured ? 'positive' : 'warning'}>
            {status.metaConfigured ? 'Meta connected' : 'Meta not configured'}
          </Badge>
          {!status.metaConfigured && (
            <span className="text-muted">
              This server has no Meta app secrets yet. See docs/manual/ad-lead-intake.md.
            </span>
          )}
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="shrink-0 text-muted">Meta callback URL</span>
          <code className="min-w-0 truncate rounded border border-line bg-surface-2 px-2 py-0.5 font-mono text-[12px]">
            {callback}
          </code>
          <Button size="sm" onClick={() => void copy(callback, 'Callback URL')}>
            Copy
          </Button>
        </div>
      </div>
    </Card>
  )
}

function MetaCard({ sources, onChanged }: { sources: AdLeadSourceItem[]; onChanged: () => void }) {
  const [editing, setEditing] = useState<AdLeadSourceItem | 'new' | undefined>()
  const [deleting, setDeleting] = useState<AdLeadSourceItem | undefined>()

  return (
    <Card>
      <CardHeader
        title="Meta Lead Ads"
        hint="One row per Meta lead form. A form that is not here holds its leads until it is."
        action={
          <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
            Map a Meta form
          </Button>
        }
      />
      {sources.length === 0 ? (
        <EmptyState
          title="No Meta forms mapped"
          hint="Map each lead form your Meta ads use. Forms already sending leads are listed first when you map one."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Form</Th>
              <Th>Goes to</Th>
              <Th>Last lead</Th>
              <Th numeric>Leads</Th>
              <Th>Swept until</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {sources.map((source) => (
              <tr key={source.id}>
                <Td>
                  <span className="font-medium">{source.name}</span>
                  <span className="block font-mono text-[11.5px] text-muted">form {source.externalFormId}</span>
                  <SourceBadges source={source} />
                </Td>
                <Td className="text-muted">
                  {source.countryIsoCode} · {assignmentText(source)}
                </Td>
                <Td className="text-muted">{source.lastReceivedAt ? dateTime(source.lastReceivedAt) : 'Never'}</Td>
                <Td numeric>{source.leadCount}</Td>
                <Td className="text-muted">{source.sweptUntil ? dateTime(source.sweptUntil) : '-'}</Td>
                <Td className="whitespace-nowrap text-right">
                  <RowActions onEdit={() => setEditing(source)} onDelete={() => setDeleting(source)} />
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <SourceDialog
        channel="MetaAds"
        source={editing}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined)
          onChanged()
        }}
      />
      <DeleteSourceDialog source={deleting} onClose={() => setDeleting(undefined)} onDeleted={onChanged} />
    </Card>
  )
}

function GoogleCard({ sources, onChanged }: { sources: AdLeadSourceItem[]; onChanged: () => void }) {
  const copy = useCopy()
  const toast = useToast()
  const [editing, setEditing] = useState<AdLeadSourceItem | 'new' | undefined>()
  const [deleting, setDeleting] = useState<AdLeadSourceItem | undefined>()
  const [rotating, setRotating] = useState<AdLeadSourceItem | undefined>()
  const [rotatingBusy, setRotatingBusy] = useState(false)
  const [shownKey, setShownKey] = useState<{ key: AdLeadSourceKey; fresh: boolean } | undefined>()
  const [importing, setImporting] = useState<AdLeadSourceItem | undefined>()

  async function rotate(source: AdLeadSourceItem) {
    setRotatingBusy(true)
    try {
      const key = await api.post<AdLeadSourceKey>(`/ad-lead-sources/${source.id}/rotate-key`)
      setRotating(undefined)
      setShownKey({ key, fresh: false })
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not rotate the key', 'error')
    } finally {
      setRotatingBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader
        title="Google Ads lead forms"
        hint="Each source has its own address and key. Paste both into a lead form asset's Webhook integration."
        action={
          <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
            Add Google source
          </Button>
        }
      />
      {sources.length === 0 ? (
        <EmptyState
          title="No Google sources"
          hint="Add one per country and owner rule. Several lead form assets can share one source."
        />
      ) : (
        <div className="flex flex-col divide-y divide-line-soft">
          {sources.map((source) => {
            const url = webhookUrl(BASE, window.location.origin, source.webhookPath ?? '')
            const test = googleTestState(source.lastTestAt, dateTime)

            return (
              <div key={source.id} className="flex flex-col gap-3 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <span className="font-medium">{source.name}</span>
                    <span className="block text-[12.5px] text-muted">
                      {source.countryIsoCode} · {assignmentText(source)}
                    </span>
                    <SourceBadges source={source} />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setImporting(source)}>
                      Import CSV
                    </Button>
                    <Button size="sm" onClick={() => setRotating(source)}>
                      Rotate key
                    </Button>
                    <RowActions onEdit={() => setEditing(source)} onDelete={() => setDeleting(source)} />
                  </div>
                </div>

                <div className="flex min-w-0 items-center gap-2 text-[13px]">
                  <span className="shrink-0 text-muted">Webhook URL</span>
                  <code className="min-w-0 truncate rounded border border-line bg-surface-2 px-2 py-0.5 font-mono text-[12px]">
                    {url}
                  </code>
                  <Button size="sm" onClick={() => void copy(url, 'Webhook URL')}>
                    Copy
                  </Button>
                </div>

                <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-[13px]">
                  <dt className="text-muted">Key</dt>
                  <dd className="text-muted">Shown once when it was made. Rotate it if it is lost.</dd>
                  <dt className="text-muted">Last test</dt>
                  <dd className={test.tone === 'positive' ? 'text-pos' : 'text-warn'}>{test.text}</dd>
                  <dt className="text-muted">Last lead</dt>
                  <dd>
                    {source.lastReceivedAt ? dateTime(source.lastReceivedAt) : 'Never'}
                    <span className="text-muted"> · {source.leadCount} leads</span>
                  </dd>
                  <dt className="text-muted">Wrong key</dt>
                  <dd className={source.rejectedKeyCount > 0 ? 'font-medium text-warn' : 'text-muted'}>
                    {source.rejectedKeyCount > 0
                      ? `${source.rejectedKeyCount} deliveries refused - an asset has an old or mistyped key`
                      : 'None refused'}
                  </dd>
                </dl>
              </div>
            )
          })}
        </div>
      )}

      <SourceDialog
        channel="GoogleAds"
        source={editing}
        onClose={() => setEditing(undefined)}
        onSaved={(key) => {
          setEditing(undefined)
          if (key) setShownKey({ key, fresh: true })
          onChanged()
        }}
      />
      <DeleteSourceDialog source={deleting} onClose={() => setDeleting(undefined)} onDeleted={onChanged} />

      <Dialog
        open={rotating !== undefined}
        title={`Rotate the key for ${rotating?.name ?? ''}`}
        description="The old key stops working at once. Every Google lead form asset using it must be given the new one, or its leads are refused."
        onClose={() => setRotating(undefined)}
        footer={
          <>
            <Button size="sm" onClick={() => setRotating(undefined)}>
              Cancel
            </Button>
            <Button size="sm" variant="danger" loading={rotatingBusy} onClick={() => rotating && void rotate(rotating)}>
              Rotate key
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">The new key is shown once, on the next screen.</p>
      </Dialog>

      <KeyDialog shown={shownKey} onClose={() => setShownKey(undefined)} />
      <CsvDialog source={importing} onClose={() => setImporting(undefined)} onImported={onChanged} />
    </Card>
  )
}

function SourceBadges({ source }: { source: AdLeadSourceItem }) {
  if (!source.isTest && source.isActive && source.failedCount === 0) return null

  return (
    <span className="mt-1 flex flex-wrap gap-1.5">
      {source.isTest && <Badge tone="warning">Test</Badge>}
      {!source.isActive && <Badge>Off</Badge>}
      {source.failedCount > 0 && <Badge tone="critical">{source.failedCount} failed</Badge>}
    </span>
  )
}

function RowActions({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <span className="inline-flex gap-2">
      <Button size="sm" onClick={onEdit}>
        Edit
      </Button>
      <Button size="sm" variant="ghost" onClick={onDelete}>
        Delete
      </Button>
    </span>
  )
}

function assignmentText(source: AdLeadSourceItem): string {
  if (source.assignmentMode === 'FixedUser') return source.assignedUserName ?? 'One person'
  if (source.assignmentMode === 'RoundRobin') return source.salesTeamName ? `${source.salesTeamName}, in turn` : 'A team, in turn'
  return ASSIGNMENT_LABELS.Unassigned
}

function emptyRequest(channel: LeadChannel, countryId: string): SaveAdLeadSourceRequest {
  return {
    channel,
    countryId,
    name: '',
    externalFormId: null,
    externalPageId: null,
    leadSourceId: null,
    assignmentMode: 'Unassigned',
    assignedUserId: null,
    salesTeamId: null,
    isTest: false,
    isActive: true,
    vipAssignmentMode: null,
    vipAssignedUserId: null,
    vipSalesTeamId: null,
  }
}

function toRequest(source: AdLeadSourceItem): SaveAdLeadSourceRequest {
  return {
    channel: source.channel,
    countryId: source.countryId,
    name: source.name,
    externalFormId: source.externalFormId,
    externalPageId: source.externalPageId,
    leadSourceId: source.leadSourceId,
    assignmentMode: source.assignmentMode,
    assignedUserId: source.assignedUserId,
    salesTeamId: source.salesTeamId,
    isTest: source.isTest,
    isActive: source.isActive,
    vipAssignmentMode: source.vipAssignmentMode ?? null,
    vipAssignedUserId: source.vipAssignedUserId ?? null,
    vipSalesTeamId: source.vipSalesTeamId ?? null,
  }
}

/**
 * Creating or editing a mapping. For Meta, the form comes from the list Meta gives - forms
 * already sending leads first, since those are the ones holding somebody's enquiry - or is
 * typed in when Meta cannot be asked.
 */
function SourceDialog({
  channel,
  source,
  onClose,
  onSaved,
}: {
  channel: LeadChannel
  source: AdLeadSourceItem | 'new' | undefined
  onClose: () => void
  onSaved: (key?: AdLeadSourceKey) => void
}) {
  const toast = useToast()
  const { current, available } = useCountry()
  const [draft, setDraft] = useState<SaveAdLeadSourceRequest>(() => emptyRequest(channel, current?.countryId ?? ''))
  const [saving, setSaving] = useState(false)
  const creating = source === 'new'
  const open = source !== undefined
  const platform = channel === 'MetaAds' ? 'Meta' : 'Google'

  useEffect(() => {
    if (source === 'new') setDraft(emptyRequest(channel, current?.countryId ?? ''))
    else if (source) setDraft(toRequest(source))
  }, [source, channel, current?.countryId])

  const forms = useAsync<MetaFormsResult | null>(
    (signal) =>
      open && creating && channel === 'MetaAds'
        ? api.get<MetaFormsResult>('/ad-lead-sources/meta/forms', undefined, signal)
        : Promise.resolve(null),
    [open, creating, channel],
  )

  const people = useAsync<LeadAssignee[]>(
    (signal) =>
      open && draft.assignmentMode === 'FixedUser' && draft.countryId
        ? api.get<LeadAssignee[]>('/leads/assignees', { countryId: draft.countryId }, signal)
        : Promise.resolve([]),
    [open, draft.assignmentMode, draft.countryId],
  )

  const teams = useAsync<SalesTeamListItem[]>(
    (signal) =>
      open && draft.assignmentMode === 'RoundRobin'
        ? api
            .get<Paged<SalesTeamListItem>>('/sales-teams', { pageSize: 200, countryId: draft.countryId }, signal)
            .then((paged) => paged.items)
        : Promise.resolve([]),
    [open, draft.assignmentMode, draft.countryId],
  )

  function set<K extends keyof SaveAdLeadSourceRequest>(key: K, value: SaveAdLeadSourceRequest[K]) {
    setDraft((d) => ({ ...d, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      if (creating) {
        const created = await api.post<AdLeadSourceKey | { id: string }>('/ad-lead-sources', draft)
        toast.show(`${draft.name || platform + ' source'} added`, 'success')
        onSaved('key' in created ? created : undefined)
      } else if (source) {
        await api.put(`/ad-lead-sources/${source.id}`, draft)
        toast.show(`${draft.name} saved`, 'success')
        onSaved()
      }
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not save', 'error')
    } finally {
      setSaving(false)
    }
  }

  const ready =
    !!draft.countryId &&
    (channel !== 'MetaAds' || !!draft.externalFormId?.trim()) &&
    (draft.assignmentMode !== 'FixedUser' || !!draft.assignedUserId) &&
    (draft.assignmentMode !== 'RoundRobin' || !!draft.salesTeamId) &&
    vipRuleReady(draft.vipAssignmentMode, draft.vipAssignedUserId, draft.vipSalesTeamId)

  return (
    <Dialog
      open={open}
      wide
      title={creating ? (channel === 'MetaAds' ? 'Map a Meta form' : 'Add a Google source') : `Edit ${draft.name}`}
      description={
        creating && channel === 'MetaAds'
          ? 'Every lead the form has already sent is processed the moment you save.'
          : creating
            ? 'You get an address and a key to paste into Google Ads. The key is shown once.'
            : undefined
      }
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!ready} onClick={() => void save()}>
            {creating ? (channel === 'MetaAds' ? 'Map form' : 'Add source') : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {creating && channel === 'MetaAds' && (
          <div className="flex flex-col gap-2">
            <p className="text-[13px] font-medium text-ink-2">Meta form</p>
            {forms.data?.problem && (
              <p className="rounded-md border border-warn/30 bg-warn-wash px-3 py-2 text-[12.5px] text-warn">
                {forms.data.problem}
              </p>
            )}
            {(forms.data?.forms ?? []).length > 0 && (
              <div className="flex max-h-56 flex-col divide-y divide-line-soft overflow-y-auto rounded-md border border-line">
                {(forms.data?.forms ?? []).map((form) => {
                  const mapped = form.mappedSourceId !== null
                  const selected = draft.externalFormId === form.formId
                  const waiting = waitingLabel(form.waitingCount)

                  return (
                    <button
                      key={form.formId}
                      type="button"
                      disabled={mapped}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          externalFormId: form.formId,
                          externalPageId: form.pageId,
                          name: d.name || form.name || `Meta form ${form.formId}`,
                        }))
                      }
                      className={cx(
                        'flex items-center justify-between gap-3 px-3 py-2 text-left text-[13px]',
                        selected ? 'bg-accent-wash' : 'hover:bg-surface-2',
                        mapped && 'cursor-not-allowed opacity-50',
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block font-medium">{form.name ?? 'Unnamed form'}</span>
                        <span className="block font-mono text-[11.5px] text-muted">
                          {form.formId}
                          {form.pageName ? ` · ${form.pageName}` : ''}
                        </span>
                      </span>
                      {mapped ? (
                        <Badge>Mapped</Badge>
                      ) : waiting ? (
                        <Badge tone="warning">{waiting}</Badge>
                      ) : null}
                    </button>
                  )
                })}
              </div>
            )}
            <Field label="Form id" required hint="Picked from the list, or typed in from Meta's Instant Forms library.">
              {(id) => (
                <Input
                  id={id}
                  inputMode="numeric"
                  value={draft.externalFormId ?? ''}
                  onChange={(e) => set('externalFormId', e.target.value.trim() || null)}
                />
              )}
            </Field>
          </div>
        )}

        {/* The kit's FormGrid pads itself for a card; inside a dialog it would sit indented
            from the form list above it. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" hint="What staff call it - for example RMS multi-outlet, Malaysia.">
            {(id) => <Input id={id} value={draft.name} onChange={(e) => set('name', e.target.value)} />}
          </Field>
          <Field label="Country" required hint={creating ? 'Fixed once saved.' : 'Fixed - its leads were made under it.'}>
            {(id) => (
              <Select id={id} disabled={!creating} value={draft.countryId} onChange={(e) => set('countryId', e.target.value)}>
                {available.map((country) => (
                  <option key={country.countryId} value={country.countryId}>
                    {country.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <PicklistField
            label="Source of lead"
            hint="Stamped on every lead, and on the customer it becomes."
            list="LeadSource"
            countryId={draft.countryId}
            value={draft.leadSourceId}
            onChange={(next) => set('leadSourceId', next)}
          />
          <Field label="Assign to">
            {(id) => (
              <Select
                id={id}
                value={draft.assignmentMode}
                onChange={(e) => set('assignmentMode', e.target.value as LeadAssignmentMode)}
              >
                <option value="Unassigned">Nobody - the country's unassigned queue</option>
                <option value="FixedUser">One person</option>
                <option value="RoundRobin">A sales team, in turn</option>
              </Select>
            )}
          </Field>
          {draft.assignmentMode === 'FixedUser' && (
            <Field label="Person" required hint="Only people who can open leads in this country.">
              {(id) => (
                <Select
                  id={id}
                  disabled={people.loading}
                  value={draft.assignedUserId ?? ''}
                  onChange={(e) => set('assignedUserId', e.target.value || null)}
                >
                  <option value="">Choose somebody</option>
                  {(people.data ?? []).map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          {draft.assignmentMode === 'RoundRobin' && (
            <Field label="Team" required hint="Each lead goes to the next member who can open leads here.">
              {(id) => (
                <Select
                  id={id}
                  disabled={teams.loading}
                  value={draft.salesTeamId ?? ''}
                  onChange={(e) => set('salesTeamId', e.target.value || null)}
                >
                  <option value="">Choose a team</option>
                  {(teams.data ?? [])
                    .filter((team) => team.isActive || team.id === draft.salesTeamId)
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          )}
          <VipAssignmentFields
            countryId={draft.countryId}
            enabled={open}
            rule={{ mode: draft.vipAssignmentMode, userId: draft.vipAssignedUserId, teamId: draft.vipSalesTeamId }}
            onChange={(rule) =>
              setDraft((d) => ({
                ...d,
                vipAssignmentMode: rule.mode,
                vipAssignedUserId: rule.userId,
                vipSalesTeamId: rule.teamId,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-3">
          <Switch
            label="Test source"
            hint="Its leads are flagged TEST, shown so you can see them arrive, and counted nowhere."
            checked={draft.isTest}
            onChange={(next) => set('isTest', next)}
          />
          <Switch
            label="Active"
            hint={`Off, ${platform} deliveries are acknowledged and not made into leads.`}
            checked={draft.isActive}
            onChange={(next) => set('isActive', next)}
          />
        </div>
      </div>
    </Dialog>
  )
}

/** A Google key, the only time anybody sees it. */
function KeyDialog({
  shown,
  onClose,
}: {
  shown: { key: AdLeadSourceKey; fresh: boolean } | undefined
  onClose: () => void
}) {
  const copy = useCopy()
  const url = shown ? webhookUrl(BASE, window.location.origin, shown.key.webhookPath) : ''

  return (
    <Dialog
      open={shown !== undefined}
      title={shown?.fresh ? 'Google source added' : 'New key'}
      description="This key is shown once. QuoteHero keeps only a fingerprint of it, so it cannot be shown again - copy it now."
      onClose={onClose}
      footer={
        <Button size="sm" variant="primary" onClick={onClose}>
          I have copied it
        </Button>
      }
    >
      {shown && (
        <div className="flex flex-col gap-4 text-[13px]">
          <CopyRow label="Webhook URL" value={url} onCopy={() => void copy(url, 'Webhook URL')} />
          <CopyRow label="Key" value={shown.key.key} onCopy={() => void copy(shown.key.key, 'Key')} />
          <ol className="list-decimal space-y-1 pl-5 text-ink-2">
            <li>In Google Ads, open the lead form asset, then Lead delivery, then Webhook integration.</li>
            <li>Paste the webhook URL and the key into the two boxes.</li>
            <li>Press Send test data. This screen then shows "Test received".</li>
          </ol>
        </div>
      )}
    </Dialog>
  )
}

function CopyRow({ label, value, onCopy }: { label: string; value: string; onCopy: () => void }) {
  return (
    <div>
      <div className="mb-1 flex items-end justify-between gap-3">
        <p className="font-medium text-ink-2">{label}</p>
        <Button size="sm" onClick={onCopy}>
          Copy
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md border border-line bg-surface-2 px-3 py-2 font-mono text-[12px] text-ink-2">
        {value}
      </pre>
    </div>
  )
}

/**
 * Leads downloaded from Google Ads, for deliveries the webhook missed. Google has no way to
 * send them again, but keeps them downloadable for a while. Importing the same file twice
 * adds nothing.
 */
function CsvDialog({
  source,
  onClose,
  onImported,
}: {
  source: AdLeadSourceItem | undefined
  onClose: () => void
  onImported: () => void
}) {
  const toast = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<GoogleCsvResult | null>(null)

  useEffect(() => {
    if (source) {
      setFile(null)
      setResult(null)
    }
  }, [source])

  async function upload() {
    if (!source || !file) return
    setBusy(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const outcome = await postForm<GoogleCsvResult>(`/lead-inbound/google-csv?sourceId=${source.id}`, form)
      setResult(outcome)
      onImported()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not import the file', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={source !== undefined}
      title={`Import leads into ${source?.name ?? ''}`}
      description="The CSV Google Ads lets you download from the lead form. Leads already here are skipped."
      onClose={onClose}
      footer={
        result ? (
          <Button size="sm" variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" loading={busy} disabled={!file} onClick={() => void upload()}>
              Import
            </Button>
          </>
        )
      }
    >
      {result ? (
        <p className="text-[13px] text-ink-2">
          {result.added} added, {result.alreadyHad} already here
          {result.skipped > 0 ? `, ${result.skipped} rows with nothing to make a lead from` : ''}.
        </p>
      ) : (
        <Field label="File" required hint="CSV, under 5 MB.">
          {(id) => (
            <Input
              id={id}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          )}
        </Field>
      )}
    </Dialog>
  )
}

function DeleteSourceDialog({
  source,
  onClose,
  onDeleted,
}: {
  source: AdLeadSourceItem | undefined
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  async function remove() {
    if (!source) return
    setBusy(true)
    try {
      await api.del(`/ad-lead-sources/${source.id}`)
      toast.show(`${source.name} deleted`, 'success')
      onClose()
      onDeleted()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not delete', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={source !== undefined}
      title={`Delete ${source?.name ?? ''}`}
      description="Only a source that never produced a lead can be deleted. One that has is turned off instead."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="danger" loading={busy} onClick={() => void remove()}>
            Delete
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        {source?.channel === 'MetaAds'
          ? 'Leads from this Meta form will be held as unmapped until it is mapped again.'
          : 'Google deliveries to this address will be refused.'}
      </p>
    </Dialog>
  )
}

const INBOUND_LABELS: Record<LeadInboundStatus, { label: string; tone: 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' }> = {
  Received: { label: 'Retrying', tone: 'warning' },
  Processed: { label: 'New lead', tone: 'positive' },
  Merged: { label: 'Added to a lead', tone: 'accent' },
  Unmapped: { label: 'Unmapped', tone: 'warning' },
  Failed: { label: 'Failed', tone: 'critical' },
  RejectedSpam: { label: 'Spam', tone: 'neutral' },
  RejectedCapped: { label: 'Over the limit', tone: 'neutral' },
  Test: { label: 'Test', tone: 'neutral' },
}

type LogFilter = '' | 'Unmapped' | 'Failed' | 'Received'

/**
 * What arrived from the ads and did not become a lead. Empty when everything works, and says
 * so. Never the answers themselves - those belong on the lead.
 */
function IntakeLog({
  log,
  onChanged,
}: {
  log: ReturnType<typeof useAsync<LeadInboundItem[]>>
  onChanged: () => void
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const [filter, setFilter] = useState<LogFilter>('')
  const [retrying, setRetrying] = useState<string | undefined>()

  const filtered = useAsync<LeadInboundItem[] | null>(
    (signal) =>
      filter ? api.get<LeadInboundItem[]>('/lead-inbound', { status: filter }, signal) : Promise.resolve(null),
    [filter],
  )

  const rows = (filter ? filtered.data : log.data) ?? []
  const error = filter ? filtered.error : log.error

  async function retry(row: LeadInboundItem) {
    setRetrying(row.id)
    try {
      const outcome = await api.post<{ status: LeadInboundStatus }>(`/lead-inbound/${row.id}/retry`)
      toast.show(`Retried - ${INBOUND_LABELS[outcome.status].label.toLowerCase()}`, 'success')
      onChanged()
      filtered.reload()
    } catch (cause) {
      // A 502 carries the platform's own reason, which is the thing worth reading.
      toast.show(cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not retry', 'error')
      onChanged()
      filtered.reload()
    } finally {
      setRetrying(undefined)
    }
  }

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <Field label="Show">
          {(id) => (
            <Select id={id} value={filter} onChange={(e) => setFilter(e.target.value as LogFilter)}>
              <option value="">Everything that needs attention</option>
              <option value="Unmapped">Unmapped - waiting for a form to be mapped</option>
              <option value="Failed">Failed - gave up after every retry</option>
              <option value="Received">Waiting to be processed</option>
            </Select>
          )}
        </Field>
      </div>

      {error && <ErrorState title={error.title} detail={error.detail} onRetry={filter ? filtered.reload : log.reload} />}

      <Busy busy={filter ? filtered.loading : log.loading}>
        <Card>
          {rows.length === 0 ? (
            <EmptyState title="Nothing waiting" hint="Every lead that arrived became a lead." />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Received</Th>
                  <Th>Channel</Th>
                  <Th>Status</Th>
                  <Th>Lead id</Th>
                  <Th>Source</Th>
                  <Th numeric>Attempts</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const status = INBOUND_LABELS[row.status]
                  const leadId = row.leadId

                  return (
                    <Tr key={row.id} onClick={leadId ? () => navigate(`/leads/${leadId}`) : undefined}>
                      <Td className="text-muted">{dateTime(row.receivedAt)}</Td>
                      <Td>
                        <Badge tone="accent">{CHANNEL_LABELS[row.channel]}</Badge>
                      </Td>
                      <Td>
                        <Badge tone={status.tone}>{status.label}</Badge>
                        {row.lastError && <span className="mt-1 block max-w-md text-[12px] text-crit">{row.lastError}</span>}
                      </Td>
                      <Td className="font-mono text-[12px]">
                        {row.externalLeadId}
                        {row.externalFormId && <span className="block text-muted">form {row.externalFormId}</span>}
                      </Td>
                      <Td className="text-muted">{row.adLeadSourceName ?? (row.status === 'Unmapped' ? 'Not mapped' : '-')}</Td>
                      <Td numeric>{row.attempts}</Td>
                      <Td className="text-right">
                        {row.status !== 'Processed' && row.status !== 'Merged' && (
                          <Button
                            size="sm"
                            loading={retrying === row.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              void retry(row)
                            }}
                          >
                            Retry
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card>
      </Busy>
    </>
  )
}
